import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

// Forward reference
const DetailCard = styled.div`
  position: absolute;
  top: ${props => props.isSearchResult ? '50%' : '160px'};
  left: ${props => props.isSearchResult ? 'calc(80% + 1rem)' : '50%'};
  transform: ${props => props.isSearchResult ? 'translateY(-20%)' : 'translateX(-50%)'};
  background: #E2FFE4;
  border: 1px solid #2C3E50;
  padding: 1.5rem;
  padding-bottom: 0.5rem;
  border-radius: 4px;
  min-width: 240px;
  z-index: 9999;
  opacity: ${props => props.isSearchResult ? '1' : '0'};
  visibility: ${props => props.isSearchResult ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
  pointer-events: auto;

  @media (max-width: 768px) {
    top: ${props => props.isSearchResult ? 'calc(100% + 1rem)' : '130px'};
    left: ${props => props.isSearchResult ? '50%' : '50%'};
    transform: ${props => props.isSearchResult ? 'translateX(-50%)' : 'translateX(-50%)'};
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const FlowerContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0rem;
  cursor: pointer;
  z-index: 1;

  &:hover {
    z-index: 1000;
  }
`;

// Update DetailCard with the hover interaction
DetailCard.defaultProps = {
  isSearchResult: false
};

const FlowerSvg = styled.div`
  width: 240px;
  height: 160px;
  position: relative;
  cursor: pointer;
  transform-origin: center center;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;

  > svg {
    transform-origin: center;
    transition: transform 0.1s ease-out;
    z-index: 1;
  }

  &:hover > svg {
    animation: spinFlower 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards;
  }
  
  /* Make sure the tooltip appears above the flower when hovering */
  &:hover + ${DetailCard} {
    opacity: 1;
    visibility: visible;
    z-index: 9999;
  }

  @keyframes spinFlower {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(90deg);
    }
  }
`;

const StockInfo = styled.div`
  text-align: center;
  margin-top: 0.2rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #2C3E50;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const TickerSymbol = styled.span`
  font-weight: 400;
`;

const PriceChange = styled.span`
  margin-left: 6px;
  transition: color 0.5s ease;
`;

const PositiveChange = styled.span`
  color:rgb(26, 146, 2);
  transition: all 0.5s ease;
`;

const NegativeChange = styled.span`
  color: #FC43D7;
  transition: all 0.5s ease;
`;

const NeutralChange = styled.span`
  color: #2C3E50;
  transition: all 0.5s ease;
`;

const PriceDisplay = styled.div`
  margin-top: 2px;
  font-size: 1.2rem;
  transition: all 0.8s ease;
  
  /* Ensure numbers fade in gracefully when changed */
  &.changed {
    animation: numberUpdate 0.8s ease-out;
  }
  
  @keyframes numberUpdate {
    0% {
      color: ${props => props.isIncreased ? 'rgb(26, 146, 2)' : props.isDecreased ? '#FC43D7' : 'inherit'};
    }
    100% {
      color: inherit;
    }
  }
`;

const DetailTitle = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #2C3E50;
  line-height: 1.4;
  text-transform: uppercase;
  font-weight: 500;
`;

const MetricLabel = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #2C3E50;
  font-weight: 500;
  margin-bottom:0.4rem;
`;

const MetricValue = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #2C3E50;
  margin-left: 0.25rem;
  font-weight: 500;
`;

const DetailText = styled.p`
  margin: 2rem 0;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1rem;
  color: #2C3E50;
  text-align: left;

  &:first-of-type {
    font-size: 1.1rem;
    margin-bottom: 1rem;
    line-height: 1.4;
    text-transform: none;
  }
`;

const Annotation = styled.span`
  display: block;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  font-style: regular;
  color: #2C3E50;
  margin-top: 0.5em;
  opacity: 0.7;
`;

const RecommendationTag = styled.div`
  padding: 0.25rem 0.5rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #2C3E50;
  border-radius: 2px;
  display: inline-block;
  border: 1px solid #2C3E50;
  background-color: ${props => {
    switch (props.type) {
      case 'buy':
        return '#38E4FF';
      case 'sell':
        return '#FC4F43';
      default:
        return '#E6F168';
    }
  }};
`;

const Legend = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 12px;
  border-top: 1px solid #eee;
  font-size: 14px;
  color: #666;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;

  .legend-content {
    display: flex;
    justify-content: center;
    gap: 24px;
    margin-bottom: 4px;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .circle {
    width: 12px;
    height: 12px;
    border-radius: 50%;
  }

  .disclaimer {
    font-size: 0.5rem;
    color: #2C3E50;
    margin-top: 4px;
    font-weight: 400;
  }
`;

const FlowerLegend = () => (
  <Legend>
    <div className="legend-content">
      <div className="legend-item">
        <div className="circle" style={{ background: '#59FF38' }} />
        Buy
      </div>
      <div className="legend-item">
        <div className="circle" style={{ background: '#FC43D72' }} />
        Sell
      </div>
      <div className="legend-item">
        <div className="circle" style={{ background: '#8368F1' }} />
        Hold
      </div>
      <div className="legend-item">
        Center size: Market Cap (2-6px, larger = bigger company)
      </div>
    </div>
    <div className="disclaimer">
      FYI these are just flowers not legal advice, the data is 15 minute delayed and the commentary like volatility, buy/sell/hold advice is AI generated
    </div>
  </Legend>
);

const Flower = ({ stock, isSearchResult }) => {
  const [hasRenderError, setHasRenderError] = useState(false);
  const [prevPrice, setPrevPrice] = React.useState(stock.price);
  const [isPriceChanged, setIsPriceChanged] = React.useState(false);
  const [direction, setDirection] = React.useState(null);
  const svgRef = useRef(null);
  
  // Detect changes in price
  React.useEffect(() => {
    if (prevPrice !== stock.price) {
      const newDirection = stock.price > prevPrice ? 'up' : stock.price < prevPrice ? 'down' : null;
      setDirection(newDirection);
      setIsPriceChanged(true);
      setPrevPrice(stock.price);
      
      // Reset the changed flag after animation
      const timer = setTimeout(() => {
        setIsPriceChanged(false);
      }, 800); // Match the CSS transition duration
      
      return () => clearTimeout(timer);
    }
  }, [stock.price, prevPrice]);

  // Enhanced error detection for SVG rendering
  useEffect(() => {
    console.log("Flower component mounted for:", stock.symbol);
    
    try {
      // Force render of a simple test path to verify SVG capability
      const testSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      const testPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
      testPath.setAttribute("d", "M 0 0 L 10 10 Z");
      testSvg.appendChild(testPath);
      
      // Check if our actual SVG is rendered correctly
      setTimeout(() => {
        if (svgRef.current) {
          const computedStyle = window.getComputedStyle(svgRef.current);
          const hasVisibleContent = svgRef.current.getBoundingClientRect().width > 0 && 
                                   svgRef.current.getBoundingClientRect().height > 0;
          
          console.log("SVG element check:", {
            symbol: stock.symbol,
            hasContent: hasVisibleContent,
            width: svgRef.current.getBoundingClientRect().width,
            height: svgRef.current.getBoundingClientRect().height
          });
          
          if (!hasVisibleContent) {
            console.warn("SVG may not be rendering correctly, enabling fallback for:", stock.symbol);
            setHasRenderError(true);
          }
        }
      }, 100);
    } catch (error) {
      console.error("SVG rendering error detected:", error);
      setHasRenderError(true);
    }
    
    return () => {
      console.log("Flower component unmounted for:", stock.symbol);
    };
  }, [stock.symbol]);

  const getRecommendation = () => {
    const change = parseFloat(stock.changePercent);
    const marketCap = parseFloat(stock.marketCapB);
    const volatility = parseFloat(stock.volatility);
    
    // Market cap categories
    const isLargeCap = marketCap >= 200;    // $200B+
    const isMidCap = marketCap >= 10;       // $10B - $200B
    const isSmallCap = marketCap < 10;      // < $10B

    // More conservative with small caps (need bigger gains to trigger buy)
    // More lenient with large caps (smaller gains can trigger buy due to stability)
    if (isLargeCap) {
      if (change > 0.5) return 'buy';      // 0.5% gain for large caps
      if (change < -1) return 'sell';      // -1% loss for large caps
    } else if (isMidCap) {
      if (change > 1) return 'buy';        // 1% gain for mid caps
      if (change < -1.5) return 'sell';    // -1.5% loss for mid caps
    } else {  // small caps
      if (change > 2 && volatility < 0.1) return 'buy';   // 2% gain and low volatility for small caps
      if (change < -2) return 'sell';      // -2% loss for small caps
    }
    
    return 'hold';
  };

  // Separate color logic from recommendation
  const getFlowerColor = () => {
    const change = parseFloat(stock.changePercent);
    if (change > 0) {
      return 'rgba(75, 255, 39, 0.7)'; // Green for up
    } else if (change < 0) {
      return 'rgba(255, 49, 214, 0.7)'; // Pink for down
    } else {
      return 'rgba(80, 24, 248, 0.7)'; // Purple for no change
    }
  };

  // Calculate number of petals based on volume
  const getNumberOfPetals = () => {
    const volume = parseInt(stock.volume);
    
    // Direct mapping to petals (4-10 range)
    if (volume <= 1000000) return 6;
    if (volume <= 2000000) return 7;
    if (volume <= 3000000) return 8;
    if (volume <= 4000000) return 9;
    if (volume <= 6000000) return 10;
    if (volume <= 8000000) return 11;
    return 12; // for volume > 8M
  };

  // Calculate petal size based on volatility
  const getPetalSize = () => {
    const volatility = parseFloat(stock.volatility);
    return 30 + (volatility * 200);
  };

  // Calculate center size based on market cap (in billions)
  const getCenterSize = () => {
    const marketCap = parseFloat(stock.marketCapB);
    // Use log scale for market cap since it varies widely
    // Scale from 3-12px based on market cap
    if (marketCap < 10) return 1;       // < $10B (small cap)
    if (marketCap < 50) return 2;       // $10B - $50B (mid cap)
    if (marketCap < 100) return 3;      // $50B - $100B (large cap)
    if (marketCap < 250) return 4;      // $100B - $250B (mega cap)
    if (marketCap < 500) return 5;     // $250B - $500B (mega cap)
    return 6;                          // > $500B (ultra mega cap)
  };

  const flowerColor = getFlowerColor();
  const slightlyBrighterColor = flowerColor.replace('0.7', '0.85');
  const numberOfPetals = getNumberOfPetals();
  const petalSize = getPetalSize();
  const centerSize = getCenterSize();

  // Generate static paths for petals instead of dynamic ones for better compatibility
  const generatePetalPaths = () => {
    const paths = [];
    const midPoint = getPetalSize() * 0.7;
    
    for (let i = 0; i < numberOfPetals; i++) {
      const angle = (i * (360 / numberOfPetals)) * (Math.PI / 180);
      
      const petalPath = `
        M 50 50
        C ${50 + Math.cos(angle - 0.6) * midPoint * 0.5} ${50 + Math.sin(angle - 0.6) * midPoint * 0.5},
          ${50 + Math.cos(angle - 0.3) * midPoint * 0.8} ${50 + Math.sin(angle - 0.3) * midPoint * 0.8},
          ${50 + Math.cos(angle) * midPoint} ${50 + Math.sin(angle) * midPoint}
        C ${50 + Math.cos(angle + 0.3) * midPoint * 0.8} ${50 + Math.sin(angle + 0.3) * midPoint * 0.8},
          ${50 + Math.cos(angle + 0.6) * midPoint * 0.5} ${50 + Math.sin(angle + 0.6) * midPoint * 0.5},
          50 50
        Z
      `;
      
      // Smaller petal size - 5% smaller
      const smallerPetalPath = `
        M 50 50
        C ${50 + Math.cos(angle - 0.6) * midPoint * 0.475} ${50 + Math.sin(angle - 0.6) * midPoint * 0.475},
          ${50 + Math.cos(angle - 0.3) * midPoint * 0.76} ${50 + Math.sin(angle - 0.3) * midPoint * 0.76},
          ${50 + Math.cos(angle) * midPoint * 0.95} ${50 + Math.sin(angle) * midPoint * 0.95}
        C ${50 + Math.cos(angle + 0.3) * midPoint * 0.76} ${50 + Math.sin(angle + 0.3) * midPoint * 0.76},
          ${50 + Math.cos(angle + 0.6) * midPoint * 0.475} ${50 + Math.sin(angle + 0.6) * midPoint * 0.475},
          50 50
        Z
      `;
      
      // Larger petal size - 5% larger
      const largerPetalPath = `
        M 50 50
        C ${50 + Math.cos(angle - 0.6) * midPoint * 0.525} ${50 + Math.sin(angle - 0.6) * midPoint * 0.525},
          ${50 + Math.cos(angle - 0.3) * midPoint * 0.84} ${50 + Math.sin(angle - 0.3) * midPoint * 0.84},
          ${50 + Math.cos(angle) * midPoint * 1.05} ${50 + Math.sin(angle) * midPoint * 1.05}
        C ${50 + Math.cos(angle + 0.3) * midPoint * 0.84} ${50 + Math.sin(angle + 0.3) * midPoint * 0.84},
          ${50 + Math.cos(angle + 0.6) * midPoint * 0.525} ${50 + Math.sin(angle + 0.6) * midPoint * 0.525},
          50 50
        Z
      `;
      
      paths.push({
        path: petalPath,
        smallerPath: smallerPetalPath,
        largerPath: largerPetalPath,
        key: i,
        duration: 3 + (i % 3)
      });
    }
    
    return paths;
  };
  
  const petalPaths = generatePetalPaths();
  
  // Only use simplified rendering if there's an actual error
  const shouldUseSimplifiedRendering = hasRenderError;

  return (
    <FlowerContainer>
      <FlowerSvg>
        {!shouldUseSimplifiedRendering ? (
          <svg 
            viewBox="0 0 100 100" 
            xmlns="http://www.w3.org/2000/svg"
            ref={svgRef}
            style={{width: '100%', height: '100%'}}
          >
            {/* Draw the petals */}
            {petalPaths.map(({path, smallerPath, largerPath, key, duration}) => (
              <path
                key={key}
                d={path}
                fill={flowerColor}
                stroke="#2C3E50"
                strokeWidth="0.5"
                strokeLinejoin="round"
                strokeLinecap="round"
              >
                <animate 
                  attributeName="d" 
                  values={`${smallerPath};${largerPath};${smallerPath}`}
                  dur={`${duration}s`} 
                  repeatCount="indefinite" 
                />
                <animate 
                  attributeName="opacity" 
                  values="0.65;0.95;0.65" 
                  dur={`${duration}s`} 
                  repeatCount="indefinite" 
                />
                <animate 
                  attributeName="fill" 
                  values={`${flowerColor};${slightlyBrighterColor};${flowerColor}`}
                  dur={`${duration}s`} 
                  repeatCount="indefinite" 
                />
              </path>
            ))}
            
            <circle cx="50" cy="50" r={centerSize} fill="#2C3E50" />
          </svg>
        ) : (
          // Simplified flower for devices with rendering issues
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: flowerColor,
            border: '2px solid #2C3E50',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{
              width: `${centerSize * 10}px`,
              height: `${centerSize * 10}px`,
              borderRadius: '50%',
              background: '#2C3E50'
            }}/>
          </div>
        )}
      </FlowerSvg>

      <DetailCard isSearchResult={isSearchResult}>
        <TitleRow>
          <DetailTitle>{stock.name}</DetailTitle>
          <RecommendationTag type={getRecommendation()}>
            {getRecommendation().toUpperCase()}
          </RecommendationTag>
        </TitleRow>
        <DetailText>
          {getRecommendation() === 'buy' 
            ? `Looking good! This is a ${
                parseFloat(stock.marketCapB) >= 200 
                  ? "very large, well-established company" 
                  : parseFloat(stock.marketCapB) >= 10
                    ? "medium-sized growing company"
                    : "smaller company with growth potential"
              }. ${
                parseInt(stock.volume) > (parseFloat(stock.marketCapB) >= 200 ? 5000000 : parseFloat(stock.marketCapB) >= 10 ? 2000000 : 1000000)
                  ? "Lots of people are trading this stock today, which supports the upward movement."
                  : "Not many trades happening yet - wait for more people to join in."
              } ${
                parseFloat(stock.volatility) < (parseFloat(stock.marketCapB) >= 200 ? 0.05 : parseFloat(stock.marketCapB) >= 10 ? 0.08 : 0.10)
                  ? "The price is moving smoothly without big swings - a healthy sign."
                  : "The price is jumping around a bit - consider buying in smaller amounts."
              }`
            : getRecommendation() === 'sell'
            ? `Proceed with caution on this ${
                parseFloat(stock.marketCapB) >= 200
                  ? "major company showing signs of weakness"
                  : parseFloat(stock.marketCapB) >= 10
                    ? "mid-sized company facing some challenges"
                    : "smaller company with increasing risks"
              }. ${
                parseInt(stock.volume) > (parseFloat(stock.marketCapB) >= 200 ? 5000000 : parseFloat(stock.marketCapB) >= 10 ? 2000000 : 1000000)
                  ? "Many investors are selling, which could push the price lower."
                  : "Trading is light - you might get better selling prices by waiting."
              } ${
                parseFloat(stock.volatility) < (parseFloat(stock.marketCapB) >= 200 ? 0.05 : parseFloat(stock.marketCapB) >= 10 ? 0.08 : 0.10)
                  ? "Even though the price isn't swinging wildly, the downward trend is concerning."
                  : "The price is making big swings, suggesting uncertainty in the market."
              }`
            : `Stay steady with this ${
                parseFloat(stock.marketCapB) >= 200
                  ? "major, stable company"
                  : parseFloat(stock.marketCapB) >= 10
                    ? "established mid-sized company"
                    : "promising smaller company"
              }. ${
                parseInt(stock.volume) > (parseFloat(stock.marketCapB) >= 200 ? 5000000 : parseFloat(stock.marketCapB) >= 10 ? 2000000 : 1000000)
                  ? "Normal amount of trading today - nothing unusual to worry about."
                  : "Trading is quiet, which is typical when the market is making up its mind."
              } ${
                parseFloat(stock.volatility) < (parseFloat(stock.marketCapB) >= 200 ? 0.05 : parseFloat(stock.marketCapB) >= 10 ? 0.08 : 0.10)
                  ? "Price is moving steadily without drama - typical for healthy stocks."
                  : "Keep an eye on the price swings - they might signal upcoming changes."
              }`
          }
        </DetailText>
        <DetailText>
          <MetricLabel>Volume:</MetricLabel> 
          <MetricValue>{parseInt(stock.volume).toLocaleString()} SHARES TRADED</MetricValue>
          <Annotation>
            {parseInt(stock.volume) > (parseFloat(stock.marketCapB) >= 200 ? 5000000 : parseFloat(stock.marketCapB) >= 10 ? 2000000 : 1000000)
              ? "High trading activity today"
              : parseInt(stock.volume) > (parseFloat(stock.marketCapB) >= 200 ? 2500000 : parseFloat(stock.marketCapB) >= 10 ? 1000000 : 500000)
              ? "Average trading activity"
              : "Lower than usual trading"
            } (shown by number of flower petals: more petals = more trading)
          </Annotation>
        </DetailText>
        <DetailText>
          <MetricLabel>Market Cap:</MetricLabel> 
          <MetricValue>${parseFloat(stock.marketCapB).toFixed(2)}B</MetricValue>
          <Annotation>
            {parseFloat(stock.marketCapB) >= 200
              ? "Very large company (over $200B)"
              : parseFloat(stock.marketCapB) >= 10
              ? "Mid-sized company ($10B-$200B)"
              : "Smaller company (under $10B)"
            } (shown by flower center size: bigger center = bigger company)
          </Annotation>
        </DetailText>
        <DetailText>
          <MetricLabel>Volatility:</MetricLabel> 
          <MetricValue>{parseFloat(stock.volatility).toFixed(2)}</MetricValue>
          <Annotation>
            {parseFloat(stock.volatility) < (parseFloat(stock.marketCapB) >= 200 ? 0.05 : parseFloat(stock.marketCapB) >= 10 ? 0.08 : 0.10)
              ? "Price moves smoothly without big swings"
              : parseFloat(stock.volatility) < (parseFloat(stock.marketCapB) >= 200 ? 0.08 : parseFloat(stock.marketCapB) >= 10 ? 0.12 : 0.15)
              ? "Normal price movements for this type of stock"
              : "Larger than usual price swings"
            } (shown by petal size: bigger petals = bigger price swings)
          </Annotation>
        </DetailText>
      </DetailCard>

      <StockInfo>
        <div>
          <TickerSymbol>{stock.symbol}</TickerSymbol>
          <PriceChange>
            {parseFloat(stock.changePercent) > 0 
              ? <PositiveChange>↑ {parseFloat(stock.changePercent).toFixed(2)}%</PositiveChange> 
              : parseFloat(stock.changePercent) < 0 
                ? <NegativeChange>↓ {Math.abs(parseFloat(stock.changePercent)).toFixed(2)}%</NegativeChange> 
                : <NeutralChange>{parseFloat(stock.changePercent).toFixed(2)}%</NeutralChange>}
          </PriceChange>
        </div>
        <PriceDisplay 
          className={isPriceChanged ? 'changed' : ''}
          isIncreased={direction === 'up'}
          isDecreased={direction === 'down'}>
          ${parseFloat(stock.price).toFixed(2)}
        </PriceDisplay>
      </StockInfo>
    </FlowerContainer>
  );
};

const MemoizedFlower = React.memo(
  Flower,
  (prevProps, nextProps) => {
    // Only re-render if specific critical properties have changed
    const isPriceEqual = prevProps.stock.price === nextProps.stock.price;
    const isChangePercentEqual = prevProps.stock.changePercent === nextProps.stock.changePercent;
    const isVolumeEqual = prevProps.stock.volume === nextProps.stock.volume;
    const isSearchResultEqual = prevProps.isSearchResult === nextProps.isSearchResult;
    
    // If all critical values are the same, don't re-render
    return isPriceEqual && isChangePercentEqual && isVolumeEqual && isSearchResultEqual;
  }
);

export default MemoizedFlower;